import React, { useCallback, useEffect, useMemo, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import Loader from "~components/Form/components/Loader";
import Info from "~components/Form/components/Info";
import { ChevronDownButton, CheckIcon, IconAlert } from "~misc/icons";


const AtingimentoCell = React.memo(function AtingimentoCell({ valor, texto }) {
  const atingiu = Math.round((valor ?? 0) * 100) >= 90;
  return (
    <span className="inline-flex items-center gap-1.5">
      {texto}
      {atingiu ? (
        <CheckIcon width={16} height={16} />
      ) : (
        <IconAlert className="w-4 h-4" />
      )}
    </span>
  );
});

const AceiteBadge = React.memo(function AceiteBadge({ aceite }) {
  if (aceite === true || aceite === "SIM") {
    return (
      <span className="inline-flex items-center rounded-full bg-[#8DC63F] px-3 py-0.5 text-[11px] font-bold text-white">
        SIM
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-[#D93000] px-3 py-0.5 text-[11px] font-bold text-white">
      NÃO
    </span>
  );
});

const cellBase = "px-3 py-3 text-[12px] font-semibold align-middle";

const tamanhoPage = 6;

const BateuLevouListing = React.memo(function BateuLevouListing({
  rows = [],
  updatedAt = "11/09/2025",
}) {
  const [expandedGroups, setExpandedGroups] = useState({});
  const [visibleCount, setVisibleCount] = useState(tamanhoPage);

  useEffect(() => {
    setVisibleCount(tamanhoPage);
  }, [rows]);

  const visibleRows = useMemo(() => rows.slice(0, visibleCount), [rows, visibleCount]);
  const hasMore = useMemo(() => visibleCount < rows.length, [visibleCount, rows.length]);

  const loadMore = useCallback(() => {
    setTimeout(() => {
      setVisibleCount((c) => Math.min(c + tamanhoPage, rows.length));
    }, 280);
  }, [rows.length]);

  const toggleGroup = useCallback((id) => {
    setExpandedGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  return (
    <section className="container mt-4">
      <InfiniteScroll
        dataLength={visibleRows.length}
        next={loadMore}
        hasMore={hasMore}
        loader={
          <div className="flex items-center justify-center py-3">
            <Loader />
          </div>
        }
        endMessage={null}
      >
      <div className="overflow-x-auto rounded-md bg-white">
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="bg-[#01054C] text-left text-white">
              <th className="px-3 py-3 text-[12px] font-semibold leading-tight">Grupo Centralizador</th>
              <th className="px-3 py-3 text-[12px] font-semibold leading-tight">Cliente</th>
              <th className="px-3 py-3 text-[12px] font-semibold leading-tight">Aceite</th>
              <th className="px-3 py-3 text-[12px] font-semibold leading-tight">Volume (t)</th>
              <th className="px-3 py-3 text-[12px] font-semibold leading-tight">Meta (t)</th>
              <th className="px-3 py-3 text-[12px] font-semibold leading-tight">
                <span className="inline-flex items-center gap-1.5">
                  Atingimento
                  <span className="inline-flex items-center justify-center rounded-full bg-white p-0.5">
                    <Info
                      bg="blue"
                      text="Considerados descontos nos atingimentos de 90% até 110%"
                      position="left"
                      tooltipClass="w-3 h-3"
                    />
                  </span>
                </span>
              </th>
              <th className="px-3 py-3 text-[12px] font-semibold leading-tight">Desconto (R$)</th>
            </tr>
          </thead>

          <tbody>
            {visibleRows.map((row) => {
              if (row.type === "flat") {
                return (
                  <tr key={row.id} className="border-b border-[#E4E8EF] text-[#091D4A]">
                    <td className={`${cellBase} font-bold`}>{row.grupoCentralizador}</td>
                    <td className={cellBase}>{row.cliente}</td>
                    <td className={cellBase}>
                      <AceiteBadge aceite={row.aceite} />
                    </td>
                    <td className={cellBase}>{row.volume}</td>
                    <td className={cellBase}>{row.meta}</td>
                    <td className={cellBase}>
                      <AtingimentoCell valor={row.atingimentoValor} texto={row.atingimento} />
                    </td>
                    <td className={cellBase}>{row.desconto}</td>
                  </tr>
                );
              }

              if (row.type === "group") {
                const isExpanded = expandedGroups[row.id] === true;

                return (
                  <React.Fragment key={row.id}>

                    <tr className="border-b border-[#E4E8EF] text-[#091D4A]">
                      <td className={`${cellBase} font-bold`}>{row.grupoCentralizador}</td>
                      <td className={cellBase}>{row.cliente}</td>
                      <td className={cellBase}>
                        <AceiteBadge aceite={row.aceite} />
                      </td>
                      <td className={cellBase}>{row.volume}</td>
                      <td className={cellBase}>{row.meta}</td>
                      <td className={cellBase}>
                        <AtingimentoCell valor={row.atingimentoValor} texto={row.atingimento} />
                      </td>
                      <td className={cellBase}>{row.desconto}</td>
                    </tr>


                    {isExpanded &&
                      row.details?.map((detail) => (
                        <tr
                          key={detail.id}
                          className="bg-[#F7F8FA] text-[#091D4A]"
                        >
                          <td className={cellBase}></td>
                          <td className={cellBase}></td>
                          <td className={`${cellBase} text-[#364153]`}>{detail.produto}</td>
                          <td className={cellBase}>{detail.volume}</td>
                          <td className={cellBase}>{detail.meta}</td>
                          <td className={cellBase}>{detail.atingimento}</td>
                          <td className={cellBase}>{detail.desconto}</td>
                        </tr>
                      ))}


                    {row.details?.length > 0 && (
                      <tr
                        className="border-b border-[#E4E8EF] cursor-pointer select-none"
                        onClick={() => toggleGroup(row.id)}
                      >
                        <td colSpan={7} className="py-2 text-center">
                          <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#364153]">
                            {isExpanded ? "Exibir menos" : "Exibir mais"}
                            <ChevronDownButton
                              className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                            />
                          </span>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              }

              return null;
            })}
          </tbody>
        </table>
      </div>
      </InfiniteScroll>

      <footer className="py-5 text-center text-[#8D96A8]">
        <p className="text-sm">
          Dados atualizados em: <span className="font-bold text-[#091D4A]">{updatedAt}</span>
        </p>
        <p className="text-sm">Atualização uma vez ao dia com referência d-1</p>
      </footer>
    </section>
  );
});

export default BateuLevouListing;
